import type {
  ContentNode,
  Index,
  Location,
  MeasuredLayout,
  PartIndex,
  TableOfContents,
  VirtualLayout,
  Xpointer,
} from "lib/types";

function measureLocation(
  element: HTMLElement,
  containerLeft: number,
  containerTop: number,
  columnWidth: number,
  columnGap: number,
  cols: number
): Location {
  const rect = element.getBoundingClientRect();
  const firstFrag = element.getClientRects()[0];
  const x = (firstFrag ?? rect).left - containerLeft;
  const y = (firstFrag ?? rect).top - containerTop;
  const columnNumber = Math.round(x / (columnWidth + columnGap));
  const startPage = Math.floor(columnNumber / cols);

  let endPage = startPage;
  const elementRight = x + rect.width;
  const bboxEndColumn = Math.round(elementRight / (columnWidth + columnGap));
  const bboxEndPage = Math.floor(bboxEndColumn / cols);

  if (bboxEndPage > startPage) {
    const frags = element.getClientRects();
    if (frags.length > 0) {
      const lastFragX = frags[frags.length - 1].left - containerLeft;
      const lastFragColumn = Math.round(lastFragX / (columnWidth + columnGap));
      endPage = Math.floor(lastFragColumn / cols);
    } else {
      endPage = bboxEndPage;
    }
  }

  return { x, y, startPage, endPage };
}

export function measureLayout(
  node: HTMLElement,
  cols: number
): MeasuredLayout | null {
  const computed = window.getComputedStyle(node);
  const columnWidth = parseFloat(computed.columnWidth);
  const columnGap = parseFloat(computed.columnGap) || 0;
  const columnsPerViewport = cols || 1;

  if (columnWidth <= 0) {
    return null;
  }

  const contentWidth = node.scrollWidth;
  const totalColumns = Math.ceil(contentWidth / (columnWidth + columnGap));
  const totalPages = Math.ceil(totalColumns / columnsPerViewport);
  const containerRect = node.getBoundingClientRect();
  const containerLeft = containerRect.left;
  const containerTop = containerRect.top;

  const parts: Record<PartIndex, Location> = {};
  const partElements = Array.from(node.children) as HTMLElement[];

  for (const [partIndex, partEl] of partElements.entries()) {
    const firstElement = partEl.querySelector("[id]") as HTMLElement;
    if (!firstElement) {
      console.warn(
        `[captureLayout] Part ${partIndex} has no [id] element, skipping!`
      );
      continue;
    }

    parts[partIndex] = measureLocation(
      firstElement,
      containerLeft,
      containerTop,
      columnWidth,
      columnGap,
      columnsPerViewport
    );
  }

  const elementsByXpointer = new Map<Xpointer, Location>();
  const allIndexedElements = node.querySelectorAll("[id]");

  for (let i = 0; i < allIndexedElements.length; i++) {
    const element = allIndexedElements[i] as HTMLElement;
    const xpointer = element.id;
    if (!xpointer) continue;

    elementsByXpointer.set(
      xpointer,
      measureLocation(
        element,
        containerLeft,
        containerTop,
        columnWidth,
        columnGap,
        columnsPerViewport
      )
    );
  }

  return {
    contentWidth,
    columnWidth,
    totalColumns,
    totalPages,
    parts,
    elementsByXpointer,
  };
}

/** Маппинг xpointer→index + сборка VirtualLayout из измерений */
export function buildVirtualLayout(
  measured: MeasuredLayout,
  content: ContentNode[][],
  toc: TableOfContents,
  firstVisiblePartIndex: number
): VirtualLayout {
  const xpointerToIndexMap = new Map<string, Index>();

  content.forEach((part, partIdx) => {
    const tocPart = toc.Parts[firstVisiblePartIndex + partIdx];
    if (!tocPart) {
      console.warn(
        `[captureLayout] partIdx=${partIdx}, tocPartIndex=${firstVisiblePartIndex + partIdx} - NO TOC PART!`
      );
      return;
    }

    part.forEach((node, nodeIdx) => {
      if (typeof node === "string") return;
      const xpointer = `/${node.xp.join("/")}.0`;
      xpointerToIndexMap.set(xpointer, tocPart.s + nodeIdx);
    });
  });

  const elements: Record<Index, Location> = {};
  for (const [xpointer, location] of measured.elementsByXpointer) {
    const index = xpointerToIndexMap.get(xpointer);
    if (index !== undefined) {
      elements[index] = location;
    }
  }

  const totalParts = toc.Parts.length;
  const measuredParts = Object.keys(measured.parts).length;
  const mode = measuredParts < totalParts ? "basic" : "virtual";

  return {
    contentWidth: measured.contentWidth,
    columnWidth: measured.columnWidth,
    elements,
    parts: measured.parts,
    totalColumns: measured.totalColumns,
    totalPages: measured.totalPages,
    mode,
    xpointerToIndex: xpointerToIndexMap,
  };
}
